import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from 'recharts';
import { ChevronLeft, ChevronRight, GripVertical } from 'lucide-react';
import useWebSocket from '../../hooks/useWebSocket';
import { usePipelineConfig } from '../../hooks/usePipelineConfig';
import { taskHistoryDb } from '../../lib/supabase';
import { synapseWorkerBridge } from '../../lib/SynapseWorkerBridge';
import type { MetricsResponse } from '../../types/worker-messages';

interface DataPoint {
  time: string;
  timestamp: number;
  value: number | null;
}

interface ChartConfig {
  id: string;
  title: string;
  metricKey: string;
  color: string;
  colorClass: string;
  gradientId: string;
  format: (v: number) => string;
  tooltipLabel: string;
  domain?: [number | string, number | string];
  referenceLine?: number;
  transform?: (v: number) => number;
}

// All available charts - add more here, they'll paginate automatically
const DEFAULT_CHARTS: ChartConfig[] = [
  {
    id: 'workers',
    title: 'Active Workers',
    metricKey: 'active_tasks',
    color: '#22c55e',
    colorClass: 'text-green-400',
    gradientId: 'colorWorkers',
    format: (v) => `${v}`,
    tooltipLabel: 'Workers',
    domain: [0, 5],
  },
  {
    id: 'completed',
    title: 'Completed Tasks',
    metricKey: 'completed_tasks',
    color: '#a855f7',
    colorClass: 'text-purple-400',
    gradientId: 'colorCompleted',
    format: (v) => `${v}`,
    tooltipLabel: 'Tasks',
  },
  {
    id: 'cpu',
    title: 'CPU Usage',
    metricKey: 'cpu_percent',
    color: '#22d3ee',
    colorClass: 'text-cyan-400',
    gradientId: 'colorCpu',
    format: (v) => `${v.toFixed(0)}%`,
    tooltipLabel: 'CPU',
    domain: [0, 'auto'],
    referenceLine: 80,
  },
  {
    id: 'memory',
    title: 'Memory',
    metricKey: 'memory_mb',
    color: '#f59e0b',
    colorClass: 'text-amber-400',
    gradientId: 'colorMemory',
    format: (v) => v >= 1024 ? `${(v / 1024).toFixed(1)}GB` : `${v.toFixed(0)}MB`,
    tooltipLabel: 'Memory',
  },
  {
    id: 'tokens',
    title: 'Total Tokens',
    metricKey: 'total_tokens',
    color: '#ec4899',
    colorClass: 'text-pink-400',
    gradientId: 'colorTokens',
    format: (v) => v >= 1000000 ? `${(v / 1000000).toFixed(2)}M` : v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${v}`,
    tooltipLabel: 'Tokens',
  },
  {
    id: 'cost',
    title: 'API Cost ($)',
    metricKey: 'total_cost_usd',
    color: '#10b981',
    colorClass: 'text-emerald-400',
    gradientId: 'colorCost',
    format: (v) => `$${v.toFixed(4)}`,
    tooltipLabel: 'Cost',
  },
];

const STORAGE_KEY = 'chart-carousel-order';
const DATA_POINTS = 60;
const POLL_INTERVAL = 1000; // 1 second refresh

const ChartCarousel: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [chartData, setChartData] = useState<Map<string, Map<number, number>>>(new Map());
  const [chartOrder, setChartOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return DEFAULT_CHARTS.map(c => c.id);
      }
    }
    return DEFAULT_CHARTS.map(c => c.id);
  });
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [historicalTotals, setHistoricalTotals] = useState({ completed_tasks: 0, total_tokens: 0, total_cost_usd: 0 });
  const { connected, lastMessage } = useWebSocket('/ws/tasks');
  const { maxWorkers } = usePipelineConfig();

  // Build ordered charts from saved order
  const orderedCharts = useMemo(() => {
    const chartMap = new Map(DEFAULT_CHARTS.map(c => [c.id, c]));
    // Filter to only include charts that still exist
    const validOrder = chartOrder.filter(id => chartMap.has(id));
    // Add any new charts not in saved order
    DEFAULT_CHARTS.forEach(c => {
      if (!validOrder.includes(c.id)) validOrder.push(c.id);
    });
    return validOrder.map(id => chartMap.get(id)!);
  }, [chartOrder]);

  // Update workers domain with actual maxWorkers
  const chartsWithConfig = useMemo(() => {
    return orderedCharts.map(c => c.id === 'workers' ? { ...c, domain: [0, maxWorkers] as [number, number] } : c);
  }, [orderedCharts, maxWorkers]);

  // 6 charts per page (2 rows x 3 columns)
  const CHARTS_PER_PAGE = 6;
  const totalPages = Math.ceil(chartsWithConfig.length / CHARTS_PER_PAGE);
  const visibleCharts = chartsWithConfig.slice(currentPage * CHARTS_PER_PAGE, currentPage * CHARTS_PER_PAGE + CHARTS_PER_PAGE);

  // Save order to localStorage
  const saveOrder = useCallback((newOrder: string[]) => {
    setChartOrder(newOrder);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newOrder));
  }, []);

  // Drag handlers
  const handleDragStart = (e: React.DragEvent, chartId: string) => {
    setDraggedId(chartId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', chartId);
    // Add some opacity to the dragged element
    (e.target as HTMLElement).style.opacity = '0.5';
  };

  const handleDragEnd = (e: React.DragEvent) => {
    setDraggedId(null);
    setDragOverId(null);
    (e.target as HTMLElement).style.opacity = '1';
  };

  const handleDragOver = (e: React.DragEvent, chartId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (chartId !== draggedId) {
      setDragOverId(chartId);
    }
  };

  const handleDragLeave = () => {
    setDragOverId(null);
  };

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    const sourceId = e.dataTransfer.getData('text/plain');

    if (sourceId && sourceId !== targetId) {
      const newOrder = [...chartOrder];
      const sourceIndex = newOrder.indexOf(sourceId);
      const targetIndex = newOrder.indexOf(targetId);

      if (sourceIndex !== -1 && targetIndex !== -1) {
        // Remove from old position
        newOrder.splice(sourceIndex, 1);
        // Insert at new position
        newOrder.splice(targetIndex, 0, sourceId);
        saveOrder(newOrder);
      }
    }

    setDraggedId(null);
    setDragOverId(null);
  };

  const fetchMetrics = useCallback(async () => {
    try {
      const response = await synapseWorkerBridge.send<MetricsResponse>('metrics:get', {});
      if (response.type !== 'response') return;
      const metrics = response.payload as any;
      const now = Date.now();
      const currentSlot = Math.floor(now / POLL_INTERVAL) * POLL_INTERVAL;

      setChartData(prev => {
        const newData = new Map(prev);

        chartsWithConfig.forEach(chart => {
          let value = metrics[chart.metricKey] ?? 0;

          // Add historical baseline for totals
          if (chart.metricKey === 'completed_tasks') {
            value += historicalTotals.completed_tasks;
          } else if (chart.metricKey === 'total_tokens') {
            value += historicalTotals.total_tokens;
          } else if (chart.metricKey === 'total_cost_usd') {
            value += historicalTotals.total_cost_usd;
          }

          if (chart.transform) value = chart.transform(value);

          const chartMap = new Map(newData.get(chart.id) || new Map());
          chartMap.set(currentSlot, value);

          // Keep only last DATA_POINTS
          const oldestValid = currentSlot - (DATA_POINTS * POLL_INTERVAL);
          for (const key of chartMap.keys()) {
            if (key < oldestValid) chartMap.delete(key);
          }

          newData.set(chart.id, chartMap);
        });

        return newData;
      });
    } catch {
      // Silent fail
    }
  }, [chartsWithConfig, historicalTotals]);

  // Fetch historical totals on mount
  useEffect(() => {
    const loadHistoricalData = async () => {
      try {
        const aggregates = await taskHistoryDb.getAggregates();
        setHistoricalTotals(aggregates);
      } catch (error) {
        console.error('Failed to load historical totals:', error);
      }
    };
    loadHistoricalData();
  }, []);

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchMetrics]);

  // NOTE: Removed useEffect that fetched on every lastMessage
  // That was causing 5k+ API calls/sec when WS had high traffic

  // Generate display data for a chart - NOW is on the RIGHT
  const generateDisplayData = useCallback((chartId: string): DataPoint[] => {
    const now = Date.now();
    const currentSlot = Math.floor(now / POLL_INTERVAL) * POLL_INTERVAL;
    const dataMap = chartData.get(chartId) || new Map();
    const slots: DataPoint[] = [];

    // Generate DATA_POINTS slots, ending at NOW (right edge)
    for (let i = DATA_POINTS - 1; i >= 0; i--) {
      const slotTime = currentSlot - (i * POLL_INTERVAL);
      const date = new Date(slotTime);
      const timeStr = date.toLocaleTimeString('en-US', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
      });

      slots.push({
        time: i === 0 ? 'NOW' : timeStr,
        timestamp: slotTime,
        value: dataMap.get(slotTime) ?? null,
      });
    }
    return slots;
  }, [chartData]);

  const getCurrentValue = (chartId: string): number => {
    const now = Date.now();
    const currentSlot = Math.floor(now / POLL_INTERVAL) * POLL_INTERVAL;
    const dataMap = chartData.get(chartId);
    if (!dataMap) return 0;

    // Try current slot first
    const currentVal = dataMap.get(currentSlot);
    if (currentVal !== undefined) return currentVal;

    // Fall back to most recent value to prevent flash to 0
    const sortedKeys = Array.from(dataMap.keys()).sort((a, b) => b - a);
    return sortedKeys.length > 0 ? dataMap.get(sortedKeys[0]) ?? 0 : 0;
  };

  const nextPage = () => setCurrentPage((p) => (p + 1) % totalPages);
  const prevPage = () => setCurrentPage((p) => (p - 1 + totalPages) % totalPages);

  return (
    <div className="relative">
      {/* Navigation - only show if multiple pages */}
      {totalPages > 1 && (
        <>
          <div className="absolute -left-2 top-1/2 -translate-y-1/2 z-20">
            <button
              onClick={prevPage}
              className="p-1 bg-[#21262d] border border-[#30363d] rounded-full hover:bg-[#30363d] transition-colors"
            >
              <ChevronLeft className="w-4 h-4 text-gray-400" />
            </button>
          </div>
          <div className="absolute -right-2 top-1/2 -translate-y-1/2 z-20">
            <button
              onClick={nextPage}
              className="p-1 bg-[#21262d] border border-[#30363d] rounded-full hover:bg-[#30363d] transition-colors"
            >
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </>
      )}

      {/* Charts Grid - 2 rows x 3 columns */}
      <div className="grid grid-cols-3 grid-rows-2 gap-3">
        {visibleCharts.map((chart) => {
          const displayData = generateDisplayData(chart.id);
          const currentValue = getCurrentValue(chart.id);
          const isDragging = draggedId === chart.id;
          const isDragOver = dragOverId === chart.id;

          return (
            <div
              key={chart.id}
              draggable
              onDragStart={(e) => handleDragStart(e, chart.id)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, chart.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, chart.id)}
              className={`bg-[#161b22]/30 backdrop-blur-xl backdrop-saturate-150 rounded-lg border p-4 flex flex-col relative overflow-hidden cursor-grab active:cursor-grabbing transition-all duration-200 ${
                isDragging ? 'opacity-50 scale-95' : ''
              } ${
                isDragOver ? 'border-cyan-500 ring-2 ring-cyan-500/30' : 'border-[#30363d]'
              }`}
              style={{ aspectRatio: '2/1' }}
            >
              {/* Drag handle indicator */}
              <div className="absolute top-2 left-2 opacity-30 hover:opacity-60 transition-opacity">
                <GripVertical className="w-3 h-3 text-gray-500" />
              </div>

              <div className="flex items-center justify-between mb-3 relative z-10">
                <h3 className="text-sm font-medium text-gray-300 pl-4">{chart.title}</h3>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500 animate-pulse' : 'bg-yellow-500'}`} />
                  <span className={`text-xs font-mono ${chart.colorClass}`}>
                    {chart.format(currentValue)}
                  </span>
                </div>
              </div>
              <div className="flex-1 relative z-10" style={{ minHeight: '120px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={displayData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id={chart.gradientId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={chart.color} stopOpacity={0.4} />
                        <stop offset="95%" stopColor={chart.color} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#30363d" strokeOpacity={0.5} />
                    <XAxis
                      dataKey="time"
                      tick={{ fill: '#6b7280', fontSize: 9 }}
                      axisLine={false}
                      tickLine={false}
                      interval={Math.floor(DATA_POINTS / 4)}
                    />
                    <YAxis
                      tick={{ fill: '#6b7280', fontSize: 10 }}
                      axisLine={false}
                      tickLine={false}
                      width={35}
                      domain={chart.domain || [0, 'auto']}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#21262d',
                        border: '1px solid #30363d',
                        borderRadius: '8px',
                      }}
                      labelStyle={{ color: '#9ca3af' }}
                      formatter={(value: any) => {
                        if (value === null || value === undefined) return ['--', 'No data'];
                        return [chart.format(value), chart.tooltipLabel];
                      }}
                    />
                    {chart.referenceLine && (
                      <ReferenceLine y={chart.referenceLine} stroke="#ef4444" strokeDasharray="3 3" strokeOpacity={0.5} />
                    )}
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke={chart.color}
                      fillOpacity={1}
                      fill={`url(#${chart.gradientId})`}
                      strokeWidth={2}
                      connectNulls={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          );
        })}
      </div>

      {/* Page indicators - only show if multiple pages */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-3">
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i)}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === currentPage ? 'bg-cyan-500' : 'bg-[#30363d] hover:bg-[#484f58]'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ChartCarousel;
